import { KeyboardEvent, ReactNode, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  CcfCopilotSuggestionIcon,
  CcfDancingDotsIcon,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { DigitalChannelType, MediaType } from '@nice-ccf/core-sdk';
import {
  ProcessAutomationConfig,
  ProcessAutomationRun,
  ProcessAutomationStatus,
  ProcessAutomationWorkflow,
} from './model/process-automation';
import {
  closeTaskPicker,
  dismissTaskResult,
  isRunActive,
  pauseWorkflowRun,
  selectProcessAutomationSession,
  selectTask,
  setTaskPanelMinimized,
  setTaskPickerQuery,
  setViewingForm,
} from './+state/ccf-process-automation.slice';
import {
  buildTemplateContext,
  cancelProcessAutomationWorkflow,
  expandTemplate,
  prefillCard,
  resumeProcessAutomationWorkflow,
  startProcessAutomationWorkflow,
  submitProcessAutomationCard,
  withTaskActions,
} from './process-automation-engine';
import { useProcessAutomationTriggers } from './use-process-automation-triggers';
import CcfProcessAutomationCard from './ccf-process-automation-card';
import CcfProcessAutomationResult from './ccf-process-automation-result';
import styles from './ccf-process-automation.module.scss';

/**
 * Checks whether Process Automation is switched on for the contact: the
 * journey enabled it in Simulator-v2, the contact is a digital chat (the only
 * media supported for now) and Copilot runs from JSON (the scripted Copilot
 * hosts the Task Assistant panel; Live LLM does not).
 * @param contact - active contact
 * @example isProcessAutomationEnabled(contact)
 * @returns boolean
 */
export const isProcessAutomationEnabled = (contact: any) => {
  const config: ProcessAutomationConfig | undefined =
    contact?.processAutomation;
  return (
    config?.enabled === true &&
    contact?.copilotSource === 'JSON' &&
    contact?.media === MediaType.DIGITAL &&
    contact?.channel === DigitalChannelType.CHAT
  );
};

/**
 * Workflows of the contact's journey
 * @param contact - active contact
 * @example getWorkflows(contact)
 * @returns ProcessAutomationWorkflow[]
 */
export const getWorkflows = (contact: any): ProcessAutomationWorkflow[] =>
  (contact?.processAutomation as ProcessAutomationConfig | undefined)
    ?.workflows ?? [];

/**
 * Filters tasks by keyword or natural language query. A single word matches
 * the name, longer queries are ranked by word overlap with name + description.
 * @param workflows - ProcessAutomationWorkflow[]
 * @param query - string
 * @example filterWorkflows(workflows, 'install')
 * @returns ProcessAutomationWorkflow[]
 */
export const filterWorkflows = (
  workflows: ProcessAutomationWorkflow[],
  query: string
) => {
  const trimmed = query.trim().toLowerCase().replace(/^\//, '');
  if (!trimmed) return workflows;
  if (!trimmed.includes(' ')) {
    return workflows.filter((workflow) =>
      `${workflow.name} ${workflow.intentName}`.toLowerCase().includes(trimmed)
    );
  }
  const words = trimmed.split(/\W+/).filter((word) => word.length > 2);
  return workflows
    .map((workflow) => {
      const haystack = `${workflow.name} ${workflow.description}`.toLowerCase();
      const score = words.filter((word) =>
        haystack.includes(word.replace(/s$/, ''))
      ).length;
      return { workflow, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((item) => item.workflow);
};

const STATUS_KEYS = {
  [ProcessAutomationStatus.IN_PROGRESS]: 'paInProgress',
  [ProcessAutomationStatus.BLOCKED]: 'paBlocked',
  [ProcessAutomationStatus.PAUSED]: 'paPaused',
  [ProcessAutomationStatus.SUCCESS]: 'paCompleted',
  [ProcessAutomationStatus.CANCELLED]: 'paCancelled',
} as const;

/**
 * Keeps typing inside task cards from reaching the scripted Copilot keyboard
 * shortcuts (arrow keys advance the demo conversation).
 * @param event - KeyboardEvent
 * @example stopFieldKeys(event)
 */
const stopFieldKeys = (event: KeyboardEvent<HTMLElement>) => {
  const target = event.target as HTMLElement;
  if (
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName) ||
    target?.isContentEditable
  ) {
    event.stopPropagation();
  }
};

export interface CcfProcessAutomationPanelProps {
  contact: any;
}

/**
 * Process Automation (Task Assistant) inside the Copilot feed: Copilot
 * collects the answers in the chat and the agent reviews and confirms the form.
 * @param props - CcfProcessAutomationPanelProps
 * @example <CcfProcessAutomationPanel />
 * @returns Task Assistant cards
 */
export function CcfProcessAutomationPanel({
  contact,
}: CcfProcessAutomationPanelProps) {
  const [translate] = useTranslator();
  const dispatch = useDispatch();
  const contactId = String(contact?.id ?? '');
  const session = useSelector(selectProcessAutomationSession(contactId));
  const workflows = useMemo(
    () => getWorkflows(contact),
    [contact?.processAutomation]
  );

  useProcessAutomationTriggers(contact, workflows, session);

  const run = session.activeRun;
  const runIsActive = isRunActive(run);
  const showRun =
    !!run && (runIsActive || !session.dismissedResultRunIds.includes(run.runId));
  const selectedWorkflow = runIsActive
    ? undefined
    : workflows.find((workflow) => workflow.id === session.selectedWorkflowId);

  if (!session.isPickerOpen && !selectedWorkflow && !showRun) {
    return null;
  }

  /**
   * Picks a task and shows its card (View Form / Run)
   * @param workflow - ProcessAutomationWorkflow
   * @example handlePick(workflow)
   */
  const handlePick = (workflow: ProcessAutomationWorkflow) => {
    if (runIsActive) return;
    dispatch(selectTask({ contactId, workflowId: workflow.id }));
  };

  /**
   * Renders a task card frame (icon, title, optional header action, body)
   * @param key - React key
   * @param icon - header icon
   * @param title - header title
   * @param body - card content
   * @param options - status caption, header action and highlight colour
   * @example renderCard('x', icon, 'Title', body)
   * @returns card JSX
   */
  const renderCard = (
    key: string,
    icon: ReactNode,
    title: string,
    body: ReactNode,
    options: {
      status?: string;
      headerAction?: ReactNode;
      borderColor?: string;
    } = {}
  ) => (
    <Box
      key={key}
      className={styles.card}
      sx={{
        borderColor: options.borderColor ?? 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Box className={styles.cardHeader}>
        {icon}
        <CcfTypography className={styles.cardTitle}>{title}</CcfTypography>
        {options.status && (
          <CcfTypography className={styles.statusText}>
            {options.status}
          </CcfTypography>
        )}
        {options.headerAction}
      </Box>
      {body}
    </Box>
  );

  /**
   * Renders the divider + right aligned buttons row of a card
   * @param buttons - button JSX
   * @example renderActions(<Button />)
   * @returns actions JSX
   */
  const renderActions = (buttons: ReactNode) => (
    <Box className={styles.actionsRow} sx={{ borderTopColor: 'divider' }}>
      {buttons}
    </Box>
  );

  /**
   * Renders the Task Assistant picker
   * @example renderPicker()
   * @returns picker JSX
   */
  const renderPicker = () =>
    renderCard(
      'picker',
      <CcfCopilotSuggestionIcon className={styles.headerIcon} />,
      translate('paTaskAssistant'),
      <>
        <TextField
          size="small"
          fullWidth
          autoFocus
          value={session.pickerQuery}
          placeholder={translate('paSearchTasks')}
          onChange={(event) =>
            dispatch(
              setTaskPickerQuery({ contactId, query: event.target.value })
            )
          }
          inputProps={{ 'aria-label': translate('paSearchTasks') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {workflows.length === 0 ? (
          <Box
            className={styles.noTasks}
            sx={{ borderColor: 'error.light', color: 'error.main' }}
            role="alert"
          >
            <Box className={styles.cardHeader}>
              <ErrorOutlineIcon fontSize="small" />
              <CcfTypography className={styles.taskName}>
                {translate('paNoTasksTitle')}
              </CcfTypography>
            </Box>
            <CcfTypography className={styles.bodyText}>
              {translate('paNoTasks')}
            </CcfTypography>
          </Box>
        ) : (
          <Box className={styles.taskList}>
            {filterWorkflows(workflows, session.pickerQuery).map(
              (workflow) => (
                <Box
                  key={workflow.id}
                  role="button"
                  tabIndex={0}
                  aria-disabled={runIsActive}
                  className={styles.taskItem}
                  sx={{
                    borderColor: 'divider',
                    opacity: runIsActive ? 0.6 : 1,
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                  onClick={() => handlePick(workflow)}
                  onKeyDown={(event) =>
                    event.key === 'Enter' && handlePick(workflow)
                  }
                >
                  <CcfTypography className={styles.taskName}>
                    {workflow.name}
                  </CcfTypography>
                  <CcfTypography className={styles.bodyText}>
                    {workflow.description}
                  </CcfTypography>
                </Box>
              )
            )}
            {filterWorkflows(workflows, session.pickerQuery).length === 0 && (
              <CcfTypography className={styles.hintText}>
                {translate('paNoMatchingTasks')}
              </CcfTypography>
            )}
            {runIsActive && (
              <CcfTypography className={styles.hintText}>
                {translate('paAnotherTaskRunning')}
              </CcfTypography>
            )}
          </Box>
        )}
      </>,
      {
        headerAction: (
          <IconButton
            size="small"
            className={styles.headerAction}
            aria-label={translate('paClose')}
            onClick={() => dispatch(closeTaskPicker({ contactId }))}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      }
    );

  /**
   * Renders the picked task waiting to be run: description with
   * View Form / Run, or its form when View Form is open
   * @param workflow - ProcessAutomationWorkflow
   * @example renderPendingTask(workflow)
   * @returns card JSX
   */
  const renderPendingTask = (workflow: ProcessAutomationWorkflow) => {
    const isViewingForm = session.isViewingForm && !!workflow.steps[0];
    const context = buildTemplateContext(contact);

    const body = isViewingForm ? (
      <>
        {workflow.formIntro && (
          <CcfTypography className={styles.formIntro}>
            {workflow.formIntro}
          </CcfTypography>
        )}
        <CcfProcessAutomationCard
          card={withTaskActions(
            prefillCard(expandTemplate(workflow.steps[0].card, context), context),
            [
              { title: translate('paBack'), command: 'back' },
              { title: translate('paRun'), command: 'run' },
            ]
          )}
          onSubmit={({ paCommand, ...values }) =>
            paCommand === 'back'
              ? dispatch(setViewingForm({ contactId, isViewingForm: false }))
              : dispatch(startProcessAutomationWorkflow(workflow, contact, values))
          }
        />
      </>
    ) : (
      <>
        <CcfTypography className={styles.bodyText}>
          {workflow.description}
        </CcfTypography>
        {runIsActive && (
          <CcfTypography className={styles.hintText}>
            {translate('paAnotherTaskRunning')}
          </CcfTypography>
        )}
        {renderActions(
          <>
            {workflow.steps[0] && (
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  dispatch(setViewingForm({ contactId, isViewingForm: true }))
                }
              >
                {translate('paViewForm')}
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={runIsActive}
              onClick={() =>
                dispatch(startProcessAutomationWorkflow(workflow, contact))
              }
            >
              {translate('paRun')}
            </Button>
          </>
        )}
      </>
    );

    return renderCard(
      `pending-${workflow.id}`,
      <AssignmentOutlinedIcon fontSize="small" color="primary" />,
      workflow.name,
      body,
      {
        headerAction: (
          <IconButton
            size="small"
            className={styles.headerAction}
            aria-label={translate('paDismiss')}
            onClick={() => dispatch(selectTask({ contactId }))}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      }
    );
  };

  /**
   * Renders the attention message or the review card of a blocked task
   * @param activeRun - ProcessAutomationRun
   * @param workflow - ProcessAutomationWorkflow | undefined
   * @example renderBlockedBody(run, workflow)
   * @returns body JSX
   */
  const renderBlockedBody = (
    activeRun: ProcessAutomationRun,
    workflow?: ProcessAutomationWorkflow
  ) => {
    /**
     * Relays review card submits to the workflow
     * @param values - submitted values
     * @example submit(values)
     */
    const submit = ({ paCommand, ...values }: Record<string, any>) => {
      if (paCommand === 'back') {
        dispatch(setTaskPanelMinimized({ contactId, isMinimized: true }));
        return;
      }
      workflow &&
        dispatch(
          submitProcessAutomationCard(workflow, contact, activeRun.runId, values)
        );
    };

    if (activeRun.requiresAttention) {
      return (
        <CcfTypography className={styles.bodyText}>
          {translate('paRequiresAttention')}
        </CcfTypography>
      );
    }
    if (!activeRun.isReviewing || !workflow?.steps[0]) return null;
    const context = buildTemplateContext(contact, activeRun.collectedValues);
    return (
      <>
        <CcfTypography className={styles.formIntro}>
          {workflow.reviewIntro || translate('paReviewIntro')}
        </CcfTypography>
        <CcfProcessAutomationCard
          card={withTaskActions(
            prefillCard(expandTemplate(workflow.steps[0].card, context), context),
            [
              { title: translate('paBack'), command: 'back' },
              { title: translate('paConfirm'), command: 'confirm' },
            ]
          )}
          onSubmit={submit}
        />
      </>
    );
  };

  /**
   * Renders the card of the current / last task for its status
   * @param activeRun - ProcessAutomationRun
   * @example renderRun(run)
   * @returns card JSX
   */
  const renderRun = (activeRun: ProcessAutomationRun) => {
    const workflow = workflows.find((item) => item.id === activeRun.workflowId);
    const isActive = isRunActive(activeRun);
    const status = activeRun.status;
    const endTaskButton = (
      <Button
        size="small"
        color="inherit"
        onClick={() =>
          dispatch(cancelProcessAutomationWorkflow(contactId, activeRun.runId))
        }
      >
        {translate('paEndTask')}
      </Button>
    );
    const resumeButton = (
      <Button
        size="small"
        variant="contained"
        disableElevation
        onClick={() =>
          workflow &&
          dispatch(
            resumeProcessAutomationWorkflow(workflow, contact, activeRun.runId)
          )
        }
      >
        {translate('paResume')}
      </Button>
    );

    let icon: ReactNode = (
      <AssignmentOutlinedIcon fontSize="small" color="primary" />
    );
    let title = activeRun.workflowName;
    let body: ReactNode = null;
    let borderColor: string | undefined;

    if (status === ProcessAutomationStatus.IN_PROGRESS) {
      icon = (
        <AutorenewIcon fontSize="small" color="primary" className={styles.spin} />
      );
      body = (
        <>
          <Box className={styles.processing}>
            <CcfTypography className={styles.bodyText}>
              {translate('paTaskInProgress')}
            </CcfTypography>
            <CcfDancingDotsIcon className={styles.processingIcon} />
          </Box>
          {renderActions(
            <>
              {endTaskButton}
              {/* The confirmed form is being sent: there is nothing to pause */}
              {!activeRun.isReviewing && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    dispatch(
                      pauseWorkflowRun({ contactId, runId: activeRun.runId })
                    )
                  }
                >
                  {translate('paPause')}
                </Button>
              )}
            </>
          )}
        </>
      );
    } else if (status === ProcessAutomationStatus.PAUSED) {
      icon = <PauseCircleOutlineIcon fontSize="small" color="primary" />;
      title = `${activeRun.workflowName} ${translate('paPaused')}`;
      body = (
        <>
          <CcfTypography className={styles.bodyText}>
            {translate('paPausedHint')}
          </CcfTypography>
          {renderActions(
            <>
              {endTaskButton}
              {resumeButton}
            </>
          )}
        </>
      );
    } else if (status === ProcessAutomationStatus.BLOCKED) {
      borderColor = activeRun.requiresAttention ? 'warning.main' : undefined;
      if (activeRun.requiresAttention) {
        icon = <WarningAmberIcon fontSize="small" color="warning" />;
      }
      body = (
        <>
          {renderBlockedBody(activeRun, workflow)}
          {activeRun.requiresAttention &&
            renderActions(
              <>
                {endTaskButton}
                {resumeButton}
              </>
            )}
        </>
      );
    } else if (status === ProcessAutomationStatus.SUCCESS) {
      icon = <CheckIcon fontSize="small" color="success" />;
      title = `${activeRun.workflowName} ${translate('paCompleted')}`;
      body = <CcfProcessAutomationResult run={activeRun} contact={contact} />;
    } else {
      icon = <ErrorOutlineIcon fontSize="small" color="error" />;
      body = (
        <CcfTypography className={styles.bodyText}>
          {translate('paCancelledHint')}
        </CcfTypography>
      );
    }

    const isMinimized = isActive && session.isMinimized;
    return renderCard(
      activeRun.runId,
      icon,
      title,
      isMinimized ? null : body,
      {
        status: translate(STATUS_KEYS[status]),
        borderColor,
        headerAction: isActive ? (
          <Tooltip
            title={isMinimized ? translate('paExpand') : translate('paCollapse')}
          >
            <IconButton
              size="small"
              className={styles.headerAction}
              onClick={() =>
                dispatch(
                  setTaskPanelMinimized({ contactId, isMinimized: !isMinimized })
                )
              }
            >
              {isMinimized ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ExpandLessIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        ) : (
          <IconButton
            size="small"
            className={styles.headerAction}
            aria-label={translate('paClose')}
            onClick={() =>
              dispatch(dismissTaskResult({ contactId, runId: activeRun.runId }))
            }
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      }
    );
  };

  return (
    <Box
      className={styles.panel}
      onKeyDown={stopFieldKeys}
      data-testid="process-automation-panel"
    >
      {selectedWorkflow && renderPendingTask(selectedWorkflow)}
      {showRun && run && renderRun(run)}
      {session.isPickerOpen && renderPicker()}
    </Box>
  );
}

export default CcfProcessAutomationPanel;
